/**
 * SFrame204 — Pay Validated Debts (dt-3): hub with five section cards (same reconcile pattern as sframe-202).
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useSettlementSession } from '../../hooks/useSettlementSession';
import { useSettlement } from '../../contexts/SettlementContext';
import {
  countPayDebtsHubDone,
  derivePayDebtsSectionsFromChildren,
  mergePayDebtsHubSections,
  reconcilePayDebtsHubFromChildren,
  type PayDebtsHubKey,
} from './debts/payDebtsHub';

type SectionCard = {
  hubKey: PayDebtsHubKey;
  title: string;
  pathForEstate: (estateId: string) => string;
};

const SECTION_CARDS: SectionCard[] = [
  { hubKey: 's1', title: 'Final Payment Preparation', pathForEstate: (e) => `/sframe-204-s1/${e}` },
  { hubKey: 's2', title: 'High-Priority Payments', pathForEstate: (e) => `/sframe-204-s2/${e}` },
  { hubKey: 's3', title: 'Tax & Government Settlements', pathForEstate: (e) => `/sframe-204-s3/${e}` },
  {
    hubKey: 's206',
    title: 'General Creditor Distributions',
    pathForEstate: (e) => `/sframe-206/${e}`,
  },
  { hubKey: 's5', title: 'Payment Documentation & Closing', pathForEstate: (e) => `/sframe-204-s5/${e}` },
];

export default function SFrame204() {
  const { estateId } = useParams<{ estateId: string }>();
  const navigate = useNavigate();
  const { isArchived } = useSettlement();
  const { loadFrameData, saveFrameData, updateStepProgress, error, isLoading } = useSettlementSession(
    estateId ?? null,
    { readOnly: isArchived }
  );

  const [hubSections, setHubSections] = useState(mergePayDebtsHubSections(undefined));

  const reloadHub = useCallback(async () => {
    if (!estateId) return;
    if (isArchived) {
      const derived = await derivePayDebtsSectionsFromChildren(loadFrameData);
      setHubSections(derived);
      return;
    }
    const merged = await reconcilePayDebtsHubFromChildren(loadFrameData, saveFrameData, updateStepProgress);
    setHubSections(merged);
  }, [estateId, isArchived, loadFrameData, saveFrameData, updateStepProgress]);

  useEffect(() => {
    void reloadHub();
  }, [reloadHub]);

  const completedCount = countPayDebtsHubDone(hubSections);
  const pct = Math.round((completedCount / 5) * 100);

  if (!estateId)
    return <div className="max-w-2xl mx-auto py-12 px-4 text-sm text-stone-600">Select an estate.</div>;

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      {error ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <h1 className="text-2xl font-semibold text-stone-800 mb-2">Pay Validated Debts</h1>

      <div className="mb-6">
        <div className="flex justify-between text-sm text-stone-500 mb-1">
          <span>{completedCount} of 5 tasks completed</span>
          <span>{pct}%</span>
        </div>
        <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
          <div
            className="h-2 bg-cyan-600 rounded-full transition-all"
            style={{ width: `${(completedCount / 5) * 100}%` }}
          />
        </div>
      </div>

      <div className="space-y-3">
        {SECTION_CARDS.map((section) => {
          const complete = !!hubSections[section.hubKey];
          const dest = estateId ? section.pathForEstate(estateId) : undefined;
          return (
            <button
              key={section.hubKey}
              type="button"
              disabled={!dest || isLoading}
              onClick={() => dest && navigate(dest)}
              className="w-full bg-white rounded-xl border border-stone-200 hover:border-cyan-500 hover:shadow-sm p-5 flex items-center gap-4 text-left transition-all disabled:opacity-50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={complete}
                readOnly
                aria-hidden
                tabIndex={-1}
                className="h-5 w-5 rounded border-stone-300 text-cyan-600 pointer-events-none shrink-0"
              />
              <span className="flex-1 font-medium text-stone-800">{section.title}</span>
              <ChevronRight className="w-5 h-5 text-stone-400 shrink-0" aria-hidden />
            </button>
          );
        })}
      </div>
    </div>
  );
}
